'use client';

import { formatCreditsNumber } from '../../utils/formatters';
import type { FC } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { ScreenContent, ScreenDisableSafeArea } from '../../screen';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { LevelBadge, getLevelColor, getLevelIcon } from '../../components/LevelBadge';
import { Credits } from '../../components/Credits';
import { useNavigation } from '../../contexts/NavigationContext';
import { useProfileData } from '../../queries/useProfileQueries';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useLevelInfoScreenData } from './useLevelInfoScreenData';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import translations from './LevelInfoScreen.translations';
import { cva } from 'class-variance-authority';
import { RiCheckboxCircleFill, RiArrowUpSLine, RiArrowDownSLine, RiTrophyLine, RiLockLine } from '../../components/icons';
import { TabFilter, type Tab } from '../../components/ui/TabFilter';

const levelMosaicColorVariants = cva('', {
  variants: {
    level: {
      levelSeedling: 'text-[var(--level-seedling-color)]',
      levelPlant: 'text-[var(--level-plant-color)]',
      levelFire: 'text-[var(--level-fire-color)]',
      levelBolt: 'text-[var(--level-bolt-color)]',
      levelStar: 'text-[var(--level-star-color)]',
      levelTrophy: 'text-[var(--level-trophy-color)]',
      levelCrown: 'text-[var(--level-crown-color)]',
      levelGem: 'text-[var(--level-gem-color)]',
      levelShield: 'text-[var(--level-shield-color)]',
      levelSword: 'text-[var(--level-sword-color)]',
      levelDiamond: 'text-[var(--level-diamond-color)]',
    },
  },
});

const levelGradientVariants = cva('', {
  variants: {
    level: {
      levelSeedling: 'bg-[image:var(--level-seedling-gradient)]',
      levelPlant: 'bg-[image:var(--level-plant-gradient)]',
      levelFire: 'bg-[image:var(--level-fire-gradient)]',
      levelBolt: 'bg-[image:var(--level-bolt-gradient)]',
      levelStar: 'bg-[image:var(--level-star-gradient)]',
      levelTrophy: 'bg-[image:var(--level-trophy-gradient)]',
      levelCrown: 'bg-[image:var(--level-crown-gradient)]',
      levelGem: 'bg-[image:var(--level-gem-gradient)]',
      levelShield: 'bg-[image:var(--level-shield-gradient)]',
      levelSword: 'bg-[image:var(--level-sword-gradient)]',
      levelDiamond: 'bg-[image:var(--level-diamond-gradient)]',
    },
  },
});

const MOSAIC_ICONS = (() => {
  const icons: { x: number; y: number; size: number; opacity: number }[] = [];
  const rings = [
    { radius: 80, count: 8, size: 18, opacity: 0.35 },
    { radius: 140, count: 12, size: 16, opacity: 0.25 },
    { radius: 200, count: 16, size: 14, opacity: 0.15 },
    { radius: 260, count: 20, size: 12, opacity: 0.08 },
  ];

  rings.forEach((ring) => {
    for (let i = 0; i < ring.count; i++) {
      const angle = (i / ring.count) * Math.PI * 2 + Math.PI / 4;
      const x = Math.cos(angle) * ring.radius;
      const y = Math.sin(angle) * ring.radius * 0.8;
      icons.push({ x, y, size: ring.size, opacity: ring.opacity });
    }
  });

  return icons;
})();

interface LevelInfoScreenProps {
  onBack?: () => void;
}

export const LevelInfoScreen: FC<LevelInfoScreenProps> = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const profile = useProfileData();
  const { xpStatus, xpConfig, levelBonuses, loading: isLoading, claimLevelBonus, claimingLevel } = useLevelInfoScreenData();

  const [activeTab, setActiveTab] = useState<'activity' | 'gameplay' | 'social'>('activity');
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const calculateDepositXp = useCallback(
    (credits: number): number => {
      if (!xpConfig || credits <= 0) return 0;
      const rubAmount = credits / 250;
      const baseXp = Math.floor((rubAmount / 100) * 5);

      const multiplierEntry = xpConfig.depositMultipliers
        .slice()
        .sort((a, b) => b.minCredits - a.minCredits)
        .find((m) => credits >= m.minCredits);

      const multiplier = multiplierEntry?.multiplier || 1;
      return Math.floor(baseXp * multiplier);
    },
    [xpConfig]
  );

  const keyLevels = useMemo(() => {
    if (!xpConfig?.keyLevels) return [];
    return xpConfig.keyLevels.map((kl) => ({
      level: kl.level,
      xp: kl.xpRequired,
      bonus: kl.bonus,
    }));
  }, [xpConfig]);

  const depositPackages = useMemo(() => {
    const packages = [25000, 50000, 125000, 250000, 625000, 1250000];
    return packages.map((credits) => ({
      coins:
        credits >= 1000000
          ? `${credits / 1000000}${t('creditsAbbrevM')}`
          : `${credits / 1000}${t('creditsAbbrevK')}`,
      totalXP: calculateDepositXp(credits),
    }));
  }, [calculateDepositXp, t]);

  if (isLoading) {
    return (
      <>
        <ScreenDisableSafeArea />
        <ScreenContent>
          <LoadingSpinner />
        </ScreenContent>
      </>
    );
  }

  const level = xpStatus?.level ?? 1;
  const totalXp = xpStatus?.totalXp ?? 0;
  const xpToNextLevel = xpStatus?.xpToNextLevel ?? 0;
  const progressPercent = xpStatus?.progressPercent ?? 0;
  const levelColorClass = getLevelColor(level);
  const LevelIcon = getLevelIcon(level);
  const avatarUrl = profile?.id ? `${getApiBaseUrl()}/api/users/avatar/${profile.id}` : undefined;

  const caseGroupsMap: Record<string, string> = {
    cheap: t('caseGroupsCheap'),
    medium: t('caseGroupsMedium'),
    expensive: t('caseGroupsExpensive'),
  };

  const activityXP = xpConfig ? [
    { action: t('rewardsDailyLogin'), xp: xpConfig.rewards.dailyLogin },
    { action: t('rewardsWeeklyStreak'), xp: xpConfig.rewards.weeklyStreak },
    ...depositPackages.map((pkg) => ({
      action: t('deposit', { amount: pkg.coins }),
      xp: pkg.totalXP,
    })),
  ] : [];

  const gameplayXP = xpConfig ? [
    { action: t('rewardsContract'), xp: xpConfig.rewards.contract },
    { action: t('rewardsUpgradeSuccess'), xp: xpConfig.rewards.upgradeSuccess },
    { action: t('rewardsUpgradeFail'), xp: xpConfig.rewards.upgradeFail },
    { action: t('rewardsDrawParticipation'), xp: xpConfig.rewards.drawParticipation },
    { action: t('rewardsDrawWin'), xp: xpConfig.rewards.drawWin },
    { action: t('rewardsCaseBattleWin'), xp: xpConfig.rewards.caseBattleWin },
    { action: t('rewardsCaseBattleLose'), xp: xpConfig.rewards.caseBattleLose },
    ...xpConfig.caseXpGroups.map((group) => ({
      action: caseGroupsMap[group.name] || group.name,
      xpRange: `${group.minXp}-${group.maxXp}`,
    })),
  ] : [];

  const socialXP = xpConfig ? [
    { action: t('rewardsReferralInvite'), xp: xpConfig.rewards.referralInvite },
    { action: t('rewardsReferralDeposit'), xp: xpConfig.rewards.referralDeposit },
  ] : [];

  const faqs = [
    { q: t('faqQ1'), a: t('faqA1') },
    { q: t('faqQ2'), a: t('faqA2') },
    { q: t('faqQ3'), a: t('faqA3') },
    { q: t('faqQ4'), a: t('faqA4') },
  ];

  const handleHistoryClick = () => {
    navigation.navigate('xp-history');
  };

  return (
    <>
      <ScreenDisableSafeArea />
      <ScreenContent>
        <div className="px-4 pb-6">
          {/* Level Icon */}
          <div className="flex flex-col items-center py-6 pt-[calc(24px+var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px))] overflow-hidden">
            <div className="relative mb-3">
              <div className="absolute top-1/2 left-1/2 pointer-events-none z-0">
                {MOSAIC_ICONS.map((icon, i) => (
                  <div
                    key={i}
                    className={`absolute top-0 left-0 ${levelMosaicColorVariants({ level: levelColorClass as any }) || ''}`}
                    style={{
                      transform: `translate(${icon.x - icon.size / 2}px, ${icon.y - icon.size / 2}px)`,
                      fontSize: icon.size,
                      opacity: icon.opacity,
                    }}
                  >
                    <LevelIcon />
                  </div>
                ))}
              </div>
              <div className={`w-[96px] h-[96px] rounded-full p-1 shadow-[0_8px_32px_rgba(0,0,0,0.3)] ${levelGradientVariants({ level: levelColorClass as any }) || ''}`}>
                <img
                  src={avatarUrl}
                  alt=""
                  className="w-full h-full rounded-full object-cover bg-slate-800"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 flex items-center justify-center w-8 h-8 rounded-full shadow-card text-white ${levelGradientVariants({ level: levelColorClass as any }) || ''}`}>
                <LevelIcon className="text-base text-white" />
              </div>
            </div>
            <div className="text-center">
              <span className="block text-2xl font-bold text-white mb-2">{t('level')} {level}</span>
              <div className="flex items-center justify-center gap-3 text-sm">
                {xpStatus?.xpRank && (
                  <>
                    <div className="flex items-center gap-1 text-white font-semibold [&>i]:text-yellow-400">
                      <RiTrophyLine className="text-yellow-400 text-base" />
                      <span>{formatCreditsNumber(xpStatus.xpRank)}</span>
                    </div>
                    <span className="text-white/40">•</span>
                  </>
                )}
                <div className="flex items-center gap-1 text-white font-semibold [&>i]:text-yellow-400">
                  <span>{formatCreditsNumber(totalXp)} XP</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Section */}
          <div className="flex flex-col gap-2 mb-6">
            <div className="rounded-3xl bg-bg-secondary p-4 flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-label-secondary text-[14px] font-medium">{t('toNextLevel')}</span>
                <span className="text-violet-400 text-[14px] font-semibold tabular-nums">{xpToNextLevel} XP</span>
              </div>
              <div className="h-2 bg-fill-tertiary rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-500 ease-out bg-violet-400"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <button
              onClick={handleHistoryClick}
              className="w-full h-[44px] rounded-full bg-accent-blue text-label-primary text-[14px] font-semibold flex items-center justify-center active:opacity-85 transition-opacity"
            >
              {t('historyTitle')}
            </button>
          </div>

          {/* How System Works */}
          <div className="mb-6">
            <h3 className="text-label-primary text-xl font-bold mb-3">{t('howItWorksTitle')}</h3>
            <div className="rounded-2xl bg-bg-secondary p-4 flex flex-col gap-3">
              {[
                t('howItWorksFeature1', { maxLevel: xpConfig?.maxLevel ?? 100 }),
                t('howItWorksFeature2'),
                t('howItWorksFeature3'),
                t('howItWorksFeature4'),
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <RiCheckboxCircleFill className="text-violet-400 text-[16px] mt-px shrink-0" />
                  <p className="m-0 text-label-secondary text-[14px] leading-snug">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Key Levels */}
          <div className="mb-6">
            <h3 className="text-label-primary text-xl font-bold mb-3">{t('keyLevels')}</h3>
            <div className="flex flex-col gap-2">
              {(levelBonuses?.bonuses ?? keyLevels).map((item) => {
                const xpValue = 'xpRequired' in item ? item.xpRequired : item.xp;
                return (
                  <div key={item.level} className="rounded-2xl bg-bg-secondary p-3 flex items-center gap-3">
                    <LevelBadge
                      level={item.level}
                      size="small"
                      showText={false}
                      className="!w-11 !h-11 !min-w-11 !p-0 !rounded-full !flex !items-center !justify-center shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-label-primary text-[15px] font-semibold leading-tight truncate">
                        {t('level')} {item.level}
                      </div>
                      <div className="text-violet-400 text-[12px] font-semibold mt-0.5 tabular-nums">
                        {formatCreditsNumber(xpValue)} XP
                      </div>
                    </div>

                    {'canClaim' in item ? (
                      item.canClaim ? (
                        <button
                          onClick={() => claimLevelBonus(item.level)}
                          disabled={claimingLevel !== null}
                          className="h-9 px-3.5 rounded-full bg-accent-blue text-label-primary text-[13px] font-semibold flex items-center gap-1 shrink-0 active:opacity-85 transition-opacity disabled:opacity-50"
                        >
                          {claimingLevel === item.level ? '…' : <Credits amount={item.bonus} iconSize={11} />}
                        </button>
                      ) : item.isForfeit ? (
                        <div className="h-9 px-3 rounded-full bg-fill-tertiary border border-separator-non-opaque text-label-tertiary text-[13px] font-medium flex items-center shrink-0">
                          —
                        </div>
                      ) : item.isClaimed ? (
                        <div className="h-9 px-3 rounded-full bg-accent-green/15 border border-accent-green/30 flex items-center shrink-0">
                          <span className="text-accent-green text-[13px] font-bold tabular-nums flex items-center gap-1">
                            <Credits amount={item.bonus} iconSize={11} />
                          </span>
                        </div>
                      ) : (
                        <div className="h-9 px-3 rounded-full bg-fill-tertiary text-label-tertiary text-[13px] font-medium flex items-center gap-1.5 shrink-0">
                          <RiLockLine className="text-[12px]" />
                          <Credits amount={item.bonus} iconSize={11} />
                        </div>
                      )
                    ) : (
                      <div className="h-9 px-3 rounded-full bg-accent-yellow/15 border border-accent-yellow/30 text-accent-yellow text-[13px] font-bold tabular-nums flex items-center gap-1 shrink-0">
                        <Credits amount={item.bonus} iconSize={11} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* XP Sources */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white m-0 mb-3">{t('xpSourcesTitle')}</h2>

            <div className="mb-4">
              <TabFilter<'activity' | 'gameplay' | 'social'>
                tabs={[
                  { id: 'activity', label: t('tabActivity') },
                  { id: 'gameplay', label: t('tabGameplay') },
                  { id: 'social', label: t('tabSocial') },
                ] satisfies Tab<'activity' | 'gameplay' | 'social'>[]}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
            </div>

            <div className="rounded-2xl bg-bg-secondary overflow-hidden">
              {(activeTab === 'activity' ? activityXP : activeTab === 'gameplay' ? gameplayXP : socialXP).map(
                (item, index, arr) => (
                  <div key={index}>
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <span className="text-label-primary text-[15px] leading-tight">{item.action}</span>
                      <span className="text-violet-400 text-[14px] font-semibold tabular-nums shrink-0">
                        +{'xpRange' in item ? item.xpRange : item.xp} XP
                      </span>
                    </div>
                    {index < arr.length - 1 && <div className="h-px bg-separator-non-opaque ml-4" />}
                  </div>
                )
              )}
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-6">
            <h2 className="text-label-primary text-xl font-bold mb-3">{t('faqTitle')}</h2>
            <div data-sound="menu_select" className="rounded-2xl bg-bg-secondary overflow-hidden">
              {faqs.map((item, idx, arr) => {
                const isOpen = faqOpen === idx;
                return (
                  <div key={idx}>
                    <button
                      onClick={() => setFaqOpen(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-fill-tertiary/40 transition-colors"
                    >
                      <span className="text-label-primary text-[15px] font-medium leading-tight">{item.q}</span>
                      {isOpen
                        ? <RiArrowUpSLine className="text-label-tertiary text-[18px] shrink-0" />
                        : <RiArrowDownSLine className="text-label-tertiary text-[18px] shrink-0" />}
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-3 text-label-secondary text-[14px] leading-relaxed">{item.a}</div>
                    )}
                    {idx < arr.length - 1 && <div className="h-px bg-separator-non-opaque ml-4" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </ScreenContent>
    </>
  );
};
